// WishArchiveTimeline — mobile timeline surface for /wishes/archive (per
// `docs/spec/wishlist/DESIGN.md §5.8` mobile fallback + W34.0 polish).
//
// Desktop ships the table (WishArchiveTable). On mobile we render a vertical
// timeline grouped by `completed_at` year — the same data, just visually
// re-shaped for a portrait viewport. Touch targets stay ≥ 44pt per
// `DESIGN.md §15`.
//
// Per-row actions (view / restore / delete) reuse the same API calls as the
// desktop table; the swipe-row UX (13.3.4) is a separate polish item.

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowRight,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  Trash2,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils/cn";
import { WISH_RESTORE_DAYS } from "@/lib/retentionWindows";
import { useToast } from "@/components/ui/toast";
import { UndoBar } from "@/components/patterns/UndoBar";
import { useUndoBar } from "@/hooks/useUndoBar";
import {
  patchWish,
  deleteWish,
  restoreWish,
  type WishCategory,
  type WishPriority,
  type WishSummary,
} from "@/lib/api/wishlist";
import { queryKeys } from "@/lib/queries/keys";

export interface WishArchiveTimelineProps {
  items: WishSummary[];
  className?: string;
}

const CATEGORY_LABEL: Record<WishCategory, string> = {
  general: "一般",
  savings: "儲蓄",
  travel: "旅行",
  event: "事件",
  purchase: "購物",
  bucket_list: "願望清單",
  custom: "自訂",
};

const PRIORITY_LABEL: Record<WishPriority, string> = {
  must: "必做",
  want: "想要",
  maybe: "也許",
};

interface GroupedYear {
  year: number;
  items: WishSummary[];
}

function groupByYear(items: WishSummary[]): GroupedYear[] {
  const map = new Map<number, WishSummary[]>();
  for (const it of items) {
    if (!it.completed_at) continue;
    const y = Number.parseInt(it.completed_at.slice(0, 4), 10);
    if (Number.isNaN(y)) continue;
    const bucket = map.get(y) ?? [];
    bucket.push(it);
    map.set(y, bucket);
  }
  return Array.from(map.entries())
    .map(([year, items]) => ({ year, items }))
    .sort((a, b) => b.year - a.year);
}

function formatMonthDay(iso: string | null): string {
  if (!iso || iso.length < 10) return "—";
  // iso is `YYYY-MM-DD...` — return `MM-DD` for the timeline marker.
  return `${iso.slice(5, 7)}-${iso.slice(8, 10)}`;
}

function priorityAccent(priority: WishPriority): {
  dot: string;
  rail: string;
} {
  switch (priority) {
    case "must":
      return { dot: "bg-ink-1", rail: "border-ink-1" };
    case "want":
      return { dot: "bg-paper-calm", rail: "border-paper-calm" };
    case "maybe":
      return { dot: "bg-hairline", rail: "border-hairline" };
  }
}

export function WishArchiveTimeline({
  items,
  className,
}: WishArchiveTimelineProps) {
  const groups = useMemo(() => groupByYear(items), [items]);

  if (items.length === 0) {
    return (
      <p
        data-testid="wish-archive-empty"
        className="text-body text-ink-3 py-s-6 text-center"
      >
        沒有符合條件的已實現心願
      </p>
    );
  }

  return (
    <ol
      data-component="WishArchiveTimeline"
      data-testid="wish-archive-timeline"
      className={cn("flex flex-col gap-s-4", className)}
    >
      {groups.map((g) => (
        <YearSection key={g.year} year={g.year} items={g.items} />
      ))}
    </ol>
  );
}

// --- Year section (collapsible on mobile) -------------------------------

interface YearSectionProps {
  year: number;
  items: WishSummary[];
}

function YearSection({ year, items }: YearSectionProps) {
  const [expanded, setExpanded] = useState<boolean>(true);
  return (
    <li
      data-component="WishArchiveTimelineYear"
      data-year={year}
      data-testid={`wish-archive-year-${year}`}
      className="bg-paper-elevated border border-hairline rounded-2xl overflow-hidden"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        data-testid={`wish-archive-year-toggle-${year}`}
        className="press w-full min-h-12 flex items-center justify-between gap-s-2 px-s-4 py-s-3 text-left"
      >
        <span className="flex items-baseline gap-s-2">
          <span className="font-display text-title text-ink-1 tabular-nums">
            {year}
          </span>
          <span className="text-meta text-ink-3 tabular-nums">
            {items.length} 個
          </span>
        </span>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-ink-2" aria-hidden />
        ) : (
          <ChevronDown className="h-4 w-4 text-ink-2" aria-hidden />
        )}
      </button>
      {expanded ? (
        <ul
          data-component="WishArchiveTimelineItems"
          className="border-t border-hairline flex flex-col"
        >
          {items.map((it) => (
            <TimelineRow key={it.id} item={it} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

// --- Per-row timeline card ----------------------------------------------

function TimelineRow({ item }: { item: WishSummary }) {
  const undo = useUndoBar<{ id: string; name: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [expanded, setExpanded] = useState<boolean>(false);
  const accent = priorityAccent(item.priority);

  const restoreMut = useMutation({
    mutationFn: async () =>
      patchWish(item.id, { status: "in_progress" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["wishes", "list"] });
      void queryClient.invalidateQueries({ queryKey: ["wishes", "archive"] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      toast.push({
        message: `已恢復「${item.name}」到進行中`,
        variant: "success",
      });
    },
    onError: () => {
      toast.push({
        message: `恢復「${item.name}」失敗，請稍後再試`,
        variant: "danger",
      });
    },
  });
  const deleteMut = useMutation({
    mutationFn: async () => deleteWish(item.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["wishes", "list"] });
      void queryClient.invalidateQueries({ queryKey: ["wishes", "archive"] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      // Was a 「已永久刪除」 toast — see the comment on the dialog below.
      undo.begin({ id: item.id, name: item.name });
    },
    onError: () => {
      toast.push({
        message: `刪除「${item.name}」失敗，請稍後再試`,
        variant: "danger",
      });
    },
  });

  // R1 (ADR-0080) follow-up. Separate from the `restoreMut` above, which
  // 「恢復到進行中」s a completed wish via patchWish — a different operation
  // from undoing a delete.
  const undoRestoreMut = useMutation({
    mutationFn: (id: string) => restoreWish(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["wishes", "list"] });
      void queryClient.invalidateQueries({ queryKey: ["wishes", "archive"] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });

  const summary = (
    <>
      <span className="text-meta text-ink-3 tabular-nums w-12 shrink-0">
        {formatMonthDay(item.completed_at)}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-body-sm font-semibold text-ink-1 truncate">
          {item.name}
        </p>
        <p className="text-meta text-ink-3 tabular-nums">
          {CATEGORY_LABEL[item.category] ?? item.category} · 進度{" "}
          {item.progress_percent}%
        </p>
      </div>
    </>
  );

  return (
    <li
      data-component="WishArchiveTimelineRow"
      data-wish-id={item.id}
      data-testid={`wish-archive-timeline-row-${item.id}`}
      className="relative pl-s-6 pr-s-4 py-s-3 border-b border-hairline last:border-b-0"
    >
      {/* Vertical rail + dot — vertical hairline behind the marker */}
      <span
        aria-hidden
        className={cn(
          "absolute left-s-4 top-0 bottom-0 border-l-2",
          accent.rail,
        )}
      />
      <span
        aria-hidden
        className={cn(
          "absolute left-[14px] top-s-4 h-2 w-2 rounded-full",
          accent.dot,
        )}
      />
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        data-testid={`wish-archive-timeline-toggle-${item.id}`}
        className="press w-full min-h-12 flex items-center gap-s-3 text-left"
      >
        {summary}
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-ink-2 shrink-0" aria-hidden />
        ) : (
          <ChevronDown className="h-4 w-4 text-ink-2 shrink-0" aria-hidden />
        )}
      </button>
      {expanded ? (
        <div
          data-component="WishArchiveTimelineRowActions"
          className="ml-[60px] mt-s-2 flex flex-wrap items-center gap-s-2"
        >
          <RowAction
            icon={ArrowRight}
            label="查看詳情"
            tone="default"
            onClick={() => navigate(`/wishes/${item.id}`)}
          />
          <RowAction
            icon={RotateCcw}
            label="恢復到進行中"
            tone="you"
            disabled={restoreMut.isPending}
            onClick={() => restoreMut.mutate()}
          />
          <RowAction
            icon={Trash2}
            label={`刪除並保留 ${WISH_RESTORE_DAYS} 天`}
            // The test id stays stable even though the label grew — it is a
            // handle for tests, not a copy of the visible text.
            testId="wish-archive-row-action-刪除"
            tone="default"
            disabled={deleteMut.isPending}
            // W56.0 J (ADR-0201) — immediate + undo, no confirm dialog.
            // `deleteWish` is a soft delete with a working `restoreWish`; the
            // dialog was a second interception in front of an exit that
            // already existed.
            onClick={() => deleteMut.mutate()}
          />
          <span className="text-meta text-ink-3 ml-auto">
            優先度 {PRIORITY_LABEL[item.priority] ?? item.priority}
          </span>
        </div>
      ) : null}
      {/* R1 (ADR-0080) follow-up. The copy here said 永久刪除 / 7 天後將從
          資料庫清除 over a `deleteWish` that is a soft delete with a working
          `restoreWish` — the wishlist module has no hard-delete route.

          W56.0 J (ADR-0201): the confirm dialog is gone — the delete runs
          immediately and the bar below is the way back. */}
      {/* The bar lives in the row but is positioned `fixed`, so it does not
          scroll with the timeline and does not need to be lifted to the list. */}
      {undo.item ? (
        <div
          data-testid="wish-archive-timeline-undo-slot"
          className="pointer-events-none fixed inset-x-0 bottom-[108px] z-40 flex justify-center px-4"
        >
          <div className="pointer-events-auto relative w-full max-w-md">
            <UndoBar
              open
              message={`已刪除「${undo.item.name}」，${WISH_RESTORE_DAYS} 天內可還原`}
              onUndo={() => {
                undoRestoreMut.mutate(undo.item!.id);
                undo.clear();
              }}
              className="inset-x-0"
            />
          </div>
        </div>
      ) : null}
    </li>
  );
}

interface RowActionProps {
  icon: LucideIcon;
  label: string;
  /**
   * Stable test handle. Defaults to the label, which is fine while the label
   * is a single word — but a label that grows (W56.0 J made the delete row say
   * how long the restore window lasts) must not silently change the handle
   * every existing test looks for.
   */
  testId?: string;
  tone: "you" | "default";
  disabled?: boolean;
  onClick: () => void;
}

function RowAction({
  icon: Icon,
  label,
  testId,
  tone,
  disabled,
  onClick,
}: RowActionProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      data-testid={testId ?? `wish-archive-row-action-${label}`}
      className={cn(
        "press min-h-11 inline-flex items-center gap-1.5 px-s-3 rounded-full text-body-sm border",
        tone === "you"
          ? "bg-ink-1 text-paper border-transparent"
          : "bg-paper text-ink-1 border-hairline hover:bg-paper-deep",
        disabled && "opacity-50 cursor-not-allowed",
      )}
    >
      <Icon className="h-4 w-4" aria-hidden />
      {label}
    </button>
  );
}